import { useState } from 'react';
import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined';
import MoreVert from '@mui/icons-material/MoreVert';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/components/PermissionGate';
import { isSimulationActive } from '@/lib/simulationStore';
import { OnlineImportDrawer } from '@/integration/onlineImport/OnlineImportDrawer';
import { ONLINE_IMPORT_PERMISSION } from '@/integration/onlineImport/onlineImportService';

interface ListViewMoreMenuProps {
  /** The entity the list shows, e.g. `Product`. */
  entity: string;
  /** The list's title, e.g. `Products`. */
  title: string;
  /** The list's route, under which a record opens at `${basePath}/${id}`. */
  basePath?: string;
  /** The entity declares `export.enabled`. */
  exportEnabled: boolean;
  /** The entity declares `import.enabled`: it has an import adapter. */
  importEnabled: boolean;
  onExport: (format: 'csv' | 'xlsx') => void;
  /** An import ran; the list refreshes. */
  onImported: () => void;
}

/**
 * A row's icon: muted like secondary text, 20px (fontSize="small"), 16px from its label — the 36px
 * box MenuItem gives a nested icon, without the theme's extra marginRight. Decorative: SvgIcon is
 * aria-hidden, the label carries the meaning.
 */
const rowIconSx = { minWidth: 36, mr: 0, color: 'text.secondary' } as const;

/**
 * The list view's ⋮ menu: export to Excel or CSV, and import from a file
 * (integration_architecture.md, Online import). Import shows for an entity with an import adapter
 * and a person holding the online-import permission, and is off in simulation mode — simulation
 * wraps every request and would report the import instead of running it.
 */
export function ListViewMoreMenu({ entity, title, basePath, exportEnabled, importEnabled, onExport, onImported }: ListViewMoreMenuProps) {
  const { t } = useTranslation();
  const mayImport = useHasPermission(ONLINE_IMPORT_PERMISSION);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [importOpen, setImportOpen] = useState(false);
  const showImport = importEnabled && mayImport;
  if (!exportEnabled && !showImport) return null;

  const simulating = isSimulationActive();
  const exportTo = (format: 'csv' | 'xlsx') => {
    setAnchor(null);
    onExport(format);
  };

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} title={t('toolbar.more', { defaultValue: 'More' })}>
        <MoreVert />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {exportEnabled && (
          <MenuItem onClick={() => exportTo('xlsx')}>
            <ListItemIcon sx={rowIconSx}><TableChartOutlined fontSize="small" /></ListItemIcon>
            <ListItemText>Export as Excel</ListItemText>
          </MenuItem>
        )}
        {exportEnabled && (
          <MenuItem onClick={() => exportTo('csv')}>
            <ListItemIcon sx={rowIconSx}><DescriptionOutlined fontSize="small" /></ListItemIcon>
            <ListItemText>Export as CSV</ListItemText>
          </MenuItem>
        )}
        {exportEnabled && showImport && <Divider />}
        {showImport && (
          <MenuItem
            disabled={simulating}
            onClick={() => { setAnchor(null); setImportOpen(true); }}
          >
            <ListItemIcon sx={rowIconSx}><FileUploadOutlined fontSize="small" /></ListItemIcon>
            <ListItemText
              primary={t('onlineImport.menuItem', { defaultValue: 'Import from Excel/CSV…' })}
              secondary={simulating ? t('onlineImport.notInSimulation', { defaultValue: 'Not available in simulation mode' }) : undefined}
            />
          </MenuItem>
        )}
      </Menu>
      {showImport && (
        <OnlineImportDrawer
          open={importOpen}
          entity={entity}
          title={title}
          basePath={basePath}
          onClose={() => setImportOpen(false)}
          onImported={onImported}
        />
      )}
    </>
  );
}
